/**
 * P135/P801 onboarding tip card (approved mock 2026-10-23): a parchment card
 * above the tab bar with one contextual tip at a time. NEXT moves on, DISMISS
 * TIP and SKIP ALL persist per device, and Settings > Replay starts the
 * sequence over. Tips and counts come from the per-mode script in
 * ../tutorial.ts - every step references a real control.
 */
import { useEffect, useRef } from 'preact/hooks';
import { useStore } from '../store';
import { activeTip, isComplete, pendingTip, scriptForMode, tutorial, useTutorialState } from '../tutorial';

export function currentMode(): string | null {
  try { return globalThis.localStorage.getItem('ea.mode'); } catch { return null; }
}

export function TipCard({ inSheet = false }: { inSheet?: boolean }) {
  const { sim } = useStore();
  const cls = inSheet ? 'tipcard tipcard-inflow' : 'tipcard';
  const cardRef = useRef<HTMLElement>(null);
  // Shell instance only: publish its real height so screen scroll areas can reserve
  // bottom space - the card keeps its exact look/position, and content can always
  // scroll fully clear of it (no occlusion of roster rows or actions).
  // Removal must happen only when the shell card truly unmounts: with an every-render
  // cleanup, Preact's deferred effects let a frame paint with the reservation gone,
  // which clamps a bottom-scrolled list up by the tip height and never restores it.
  useEffect(() => {
    if (inSheet) return;
    return () => { document.documentElement.style.removeProperty('--tip-h'); };
  }, [inSheet]);
  useEffect(() => {
    if (inSheet) return;
    const el = cardRef.current;
    if (el) document.documentElement.style.setProperty('--tip-h', `${String(el.offsetHeight + 10)}px`);
  });
  const st = useTutorialState();
  const mode = currentMode();
  const script = scriptForMode(mode);
  const active = activeTip(script, st, sim);
  const complete = isComplete(script, st, sim);

  useEffect(() => {
    if (!st.finished && !st.skippedAll && complete) tutorial.markFinished();
  }, [st, complete]);

  if (!active) {
    // Sequence is waiting on a gate: name the unfinished step the player was pointed at,
    // so NEXT never reads as the tutorial silently ending.
    const pending = pendingTip(script, st, sim);
    if (!pending) return null;
    return (
      <section ref={cardRef} class={cls} aria-label="First steps, waiting">
        <div class="eyebrow tipcard-eyebrow">FIRST STEPS - WAITING ON YOU</div>
        <p class="tipcard-body">
          <span>Finish this first: </span>
          {pending.body(sim).map((seg, i) => (seg.bold ? <strong key={i}>{seg.text}</strong> : <span key={i}>{seg.text}</span>))}
        </p>
        <div class="tipcard-actions">
          <button class="btn btn-quiet" style="flex:1" onClick={() => { tutorial.dismiss(pending.id); }}>Dismiss tip</button>
          <button class="btn btn-quiet" style="flex:1" onClick={() => { tutorial.skipAll(); }}>Skip all</button>
        </div>
      </section>
    );
  }
  const { tip, index, total } = active;
  return (
    <section ref={cardRef} class={cls} aria-label={`First steps, tip ${String(index)} of ${String(total)}`}>
      <div class="eyebrow tipcard-eyebrow">FIRST STEPS - {index} OF {total}</div>
      <p class="tipcard-body">
        {tip.body(sim).map((seg, i) => (seg.bold ? <strong key={i}>{seg.text}</strong> : <span key={i}>{seg.text}</span>))}
      </p>
      <div class="tipcard-actions">
        <button class="btn btn-primary" style="flex:1.4" onClick={() => { tutorial.next(tip.id); }}>Next</button>
        <button class="btn btn-quiet" style="flex:1" onClick={() => { tutorial.dismiss(tip.id); }}>Dismiss tip</button>
        <button class="btn btn-quiet" style="flex:1" onClick={() => { tutorial.skipAll(); }}>Skip all</button>
      </div>
      <div class="tipcard-foot">Dismissed tips never return - replay any time from Settings.</div>
    </section>
  );
}
